import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, billing as billingApi, type BillingPlan } from "@/lib/api";

/**
 * `/billing` — current tier, usage, and the upgrade / manage subscription
 * affordances.
 *
 * - Free users: usage bar + "Upgrade to Pro" with a monthly/annual toggle.
 *   Clicking redirects to Stripe Checkout. Stripe redirects back here with
 *   `?checkout=success` after payment; we poll `/api/billing/me` until the
 *   webhook flips the tier.
 *
 * - Pro users: usage bar + "Manage subscription" → Stripe Customer Portal.
 *   Cancellation surfaces as "Pro until <tier_until>" so the user sees
 *   exactly when they revert to free.
 */
export function Billing() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutFlag = searchParams.get("checkout"); // "success" | "cancel" | null
  const [plan, setPlan] = useState<BillingPlan>("monthly");

  const meQuery = useQuery({
    queryKey: ["billing_me"],
    queryFn: () => billingApi.me(),
    // After a successful checkout, poll for tier flip until it lands.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (checkoutFlag === "success" && data?.tier !== "pro") {
        return 1500;
      }
      return false;
    },
  });

  const checkoutMut = useMutation({
    mutationFn: (chosen: BillingPlan) => billingApi.createCheckout(chosen),
    onSuccess: (res) => {
      // Hand the user off to Stripe.
      window.location.href = res.url;
    },
  });

  const portalMut = useMutation({
    mutationFn: () => billingApi.createPortal(),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });

  // Once the webhook flips the tier, drop the `?checkout=success` flag so a
  // refresh doesn't keep showing the post-checkout banner indefinitely.
  useEffect(() => {
    if (checkoutFlag === "success" && meQuery.data?.tier === "pro") {
      void qc.invalidateQueries({ queryKey: ["billing_me"] });
      const next = new URLSearchParams(searchParams);
      next.delete("checkout");
      setSearchParams(next, { replace: true });
    }
  }, [checkoutFlag, meQuery.data?.tier, qc, searchParams, setSearchParams]);

  const data = meQuery.data;
  // Qualitative bands map to a coarse progress-bar width + color. We
  // deliberately don't expose the raw monthly_used / monthly_limit numbers
  // (they're env-tunable internal infrastructure). The bar gives shape,
  // not specifics — same approach Anthropic / OpenAI use.
  const usageBand = useMemo(() => {
    if (!data) return { width: 0, color: "bg-emerald-500", copy: "" };
    switch (data.usage_state) {
      case "unlimited":
        return {
          width: 0,
          color: "bg-emerald-500",
          copy: "No usage cap on your plan.",
        };
      case "at_limit":
        return {
          width: 100,
          color: "bg-red-500",
          copy: "You've reached your monthly limit. Pro raises it substantially.",
        };
      case "approaching_limit":
        return {
          width: 85,
          color: "bg-amber-500",
          copy: "Approaching your monthly limit.",
        };
      case "active":
        return {
          width: 65,
          color: "bg-emerald-500",
          copy: "Active use this month.",
        };
      case "comfortable":
      default:
        return {
          width: 25,
          color: "bg-emerald-500",
          copy: "Plenty of headroom this month.",
        };
    }
  }, [data]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <header className="mb-6">
        <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100">
          ← Back
        </Link>
        <h1 className="text-2xl font-semibold mt-2">Billing & usage</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Manage your Coauthor subscription and see how much of your monthly
          quota you've used.
        </p>
      </header>

      {checkoutFlag === "success" && data?.tier !== "pro" && (
        <div className="mb-6 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-700 p-3 text-sm">
          Payment received — finalising your account…
        </div>
      )}
      {checkoutFlag === "success" && data?.tier === "pro" && (
        <div className="mb-6 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-700 p-3 text-sm">
          🎉 You're on Pro. Limits raised — happy drafting.
        </div>
      )}
      {checkoutFlag === "cancel" && (
        <div className="mb-6 rounded-md border border-zinc-200 dark:border-zinc-800 p-3 text-sm">
          Checkout cancelled. You can upgrade any time.
        </div>
      )}

      {meQuery.isLoading && <div className="text-sm text-zinc-500">Loading…</div>}
      {meQuery.error instanceof ApiError && (
        <div className="text-sm text-red-600">
          {(meQuery.error.body as { error?: string } | null)?.error ?? "Could not load billing."}
        </div>
      )}

      {data && (
        <>
          {/* Current plan card */}
          <section className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-zinc-500">Current plan</div>
                <div className="text-lg font-semibold capitalize">{data.tier}</div>
              </div>
              <TierBadge
                tier={data.tier}
                subscriptionStatus={data.subscription_status}
                tierUntil={data.tier_until}
              />
            </div>

            {/* Usage — qualitative state only. No raw counts, no caps. The
                actual numbers are env-tunable internal infrastructure;
                exposing them would anchor users on specifics we want to
                keep flexible. Matches Anthropic / OpenAI's opacity. */}
            <div>
              <div className="text-xs text-zinc-500 mb-1">Monthly usage</div>
              <div className="h-2 bg-zinc-100 dark:bg-zinc-800 rounded">
                <div
                  className={`h-2 ${usageBand.color} rounded transition-all`}
                  style={{ width: `${usageBand.width}%` }}
                />
              </div>
              <div className="mt-2 text-xs text-zinc-500">{usageBand.copy}</div>
            </div>
          </section>

          {/* Upgrade or manage */}
          {data.tier === "free" && data.billing_configured && (
            <section className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4">
              <h2 className="text-lg font-semibold mb-1">Upgrade to Pro</h2>
              <p className="text-sm text-zinc-500 mb-4">
                The workbench, fully open: unlimited docs and collaboration,
                generous integrated-model review allowance, small-tier
                reviews unmetered. Cancel anytime — you'll keep Pro through
                the end of the period you paid for.
              </p>
              <div className="flex items-center gap-2 mb-4 text-sm">
                <button
                  type="button"
                  onClick={() => setPlan("monthly")}
                  className={`px-3 py-1.5 rounded-md border ${
                    plan === "monthly"
                      ? "bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white border-zinc-900"
                      : "border-zinc-300 dark:border-zinc-700"
                  }`}
                >
                  $15 / month
                </button>
                <button
                  type="button"
                  onClick={() => setPlan("annual")}
                  className={`px-3 py-1.5 rounded-md border ${
                    plan === "annual"
                      ? "bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white border-zinc-900"
                      : "border-zinc-300 dark:border-zinc-700"
                  }`}
                >
                  $150 / year <span className="text-xs opacity-70">(save 17%)</span>
                </button>
              </div>
              <button
                type="button"
                onClick={() => checkoutMut.mutate(plan)}
                disabled={checkoutMut.isPending}
                className="rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {checkoutMut.isPending ? "Redirecting…" : "Upgrade with Stripe"}
              </button>
              {checkoutMut.error instanceof ApiError && (
                <div className="mt-3 text-sm text-red-600">
                  {(checkoutMut.error.body as { error?: string } | null)?.error ??
                    "Could not start checkout."}
                </div>
              )}
            </section>
          )}

          {data.tier === "free" && !data.billing_configured && (
            <section className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-4 text-sm">
              Self-serve upgrades are coming soon — for now, hit
              <a href="mailto:hello@coauthor.skarp.app" className="underline ml-1">
                hello@coauthor.skarp.app
              </a>{" "}
              and we'll lift your tier manually.
            </section>
          )}

          {(data.tier === "pro" || data.stripe_customer_id_present) && (
            <section className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4">
              <h2 className="text-lg font-semibold mb-1">Manage subscription</h2>
              <p className="text-sm text-zinc-500 mb-4">
                Update your payment method, change plan, view invoices, or
                cancel — all in Stripe's Customer Portal.
              </p>
              <button
                type="button"
                onClick={() => portalMut.mutate()}
                disabled={portalMut.isPending}
                className="rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-2 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
              >
                {portalMut.isPending ? "Opening…" : "Open Customer Portal →"}
              </button>
              {portalMut.error instanceof ApiError && (
                <div className="mt-3 text-sm text-red-600">
                  {(portalMut.error.body as { error?: string } | null)?.error ??
                    "Could not open portal."}
                </div>
              )}
            </section>
          )}

          {/* Integrated-model credits (Phase 12.9). Hidden for internal tier
              (unlimited; no balance to show). */}
          {!data.model_credits_unlimited && (
            <ModelCreditsSection data={data} />
          )}
        </>
      )}

      {/* Legal links — required by the Anthropic Connector Directory and
          generally good hygiene. Render at the bottom of every billing view
          (signed-in, signed-out, error states) so a reviewer can always
          reach them in one click. */}
      <footer className="mt-10 pt-4 border-t border-zinc-200 dark:border-zinc-800 text-xs text-zinc-500 flex flex-wrap gap-x-4 gap-y-1">
        <Link to="/privacy" className="hover:underline">
          Privacy Policy
        </Link>
        <Link to="/terms" className="hover:underline">
          Terms of Service
        </Link>
        <a
          href="mailto:support@skarp.app"
          className="hover:underline"
        >
          Contact support
        </a>
      </footer>
    </div>
  );
}

/**
 * Integrated-model reviews — qualitative state + audit list of recent
 * reviews. Per the opacity-first pricing policy we don't expose the raw
 * credit balance or per-call costs here; specifics live in the audit list
 * only ("for the curious"). Free-tier users see trial messaging + locked
 * rows for tier-gated models with inline upgrade CTAs.
 */
function ModelCreditsSection({
  data,
}: {
  data: import("@/lib/api").BillingMeResponse;
}) {
  const reviewsQuery = useQuery({
    queryKey: ["billing_model_reviews"],
    queryFn: () => billingApi.modelReviews(20),
  });

  const nextResetLabel = data.next_reset_at
    ? new Date(data.next_reset_at).toLocaleDateString()
    : null;

  const stateChrome =
    data.credit_state === "depleted"
      ? {
          pill: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
          headline: "Out of integrated-model reviews this month.",
          sub: nextResetLabel
            ? `Resets ${nextResetLabel}. Or upgrade to Heavy for more.`
            : "Your free-tier trial is used up. Upgrade to Pro for an ongoing monthly allowance.",
        }
      : data.credit_state === "low"
        ? {
            pill: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
            headline: "You've used most of your monthly review allowance.",
            sub: nextResetLabel
              ? `Resets ${nextResetLabel}.`
              : "Resets next month.",
          }
        : data.credit_state === "unlimited"
          ? {
              pill: "bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
              headline: "Unmetered model reviews on your account.",
              sub: "Internal tier — no review allowance applies.",
            }
          : {
              pill: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
              headline:
                data.tier === "free"
                  ? "Generous integrated-model trial active."
                  : "Substantial monthly review allowance.",
              sub: nextResetLabel
                ? `Resets ${nextResetLabel}.`
                : data.model_credits_trial_granted
                  ? "Free-tier trial active. Upgrade to Pro for an ongoing monthly allowance."
                  : "You'll be granted a generous trial on your first review.",
            };

  return (
    <section className="mt-6 rounded-md border border-zinc-200 dark:border-zinc-800 p-4">
      <h2 className="text-lg font-semibold mb-1">Integrated model reviews</h2>
      <p className="text-sm text-zinc-500 mb-4">
        Ask Claude or GPT (hosted by Coauthor) to review your work without
        copy-pasting to another tool. Allowance scales with your tier; the
        platform handles the spend behind the scenes.
      </p>

      <div className="flex items-start gap-3 mb-4">
        <span
          className={`inline-block rounded-md px-2 py-1 text-xs font-medium ${stateChrome.pill}`}
        >
          {stateChrome.headline}
        </span>
        <span className="text-xs text-zinc-500 mt-1">{stateChrome.sub}</span>
      </div>

      {data.available_models.length > 0 && (
        <div className="mb-3 text-sm">
          <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5">
            Available on your tier
          </div>
          <ul className="grid sm:grid-cols-2 gap-1.5 text-xs">
            {data.available_models.map((m) => (
              <li
                key={m.model_id}
                className="flex items-center justify-between rounded border border-zinc-200 dark:border-zinc-800 px-2 py-1"
              >
                <span className="truncate">
                  <span className="font-medium">{m.display_name}</span>{" "}
                  <span className="text-zinc-500">({m.tier})</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.models_requiring_upgrade.length > 0 && (
        <div className="mb-3 text-sm">
          <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1.5">
            Available on {data.models_requiring_upgrade[0].upgrade_required === "pro" ? "Pro" : "Heavy"}+
          </div>
          <ul className="grid sm:grid-cols-2 gap-1.5 text-xs">
            {data.models_requiring_upgrade.map((m) => (
              <li
                key={m.model_id}
                className="flex items-center justify-between rounded border border-dashed border-zinc-300 dark:border-zinc-700 px-2 py-1 text-zinc-500"
              >
                <span className="truncate">
                  <span className="font-medium">{m.display_name}</span>{" "}
                  <span>({m.tier})</span>
                </span>
                <span className="ml-2 text-[10px] uppercase tracking-wide flex-shrink-0">
                  🔒 {m.upgrade_required === "pro" ? "Pro" : "Heavy"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <h3 className="text-sm font-medium mt-4 mb-2">Recent reviews</h3>
      {reviewsQuery.isLoading && (
        <div className="text-xs text-zinc-500">Loading…</div>
      )}
      {reviewsQuery.data && reviewsQuery.data.reviews.length === 0 && (
        <div className="text-xs text-zinc-500">No reviews yet.</div>
      )}
      {reviewsQuery.data && reviewsQuery.data.reviews.length > 0 && (
        <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 text-xs rounded-md border border-zinc-200 dark:border-zinc-800">
          {reviewsQuery.data.reviews.map((r) => (
            <li
              key={r.id}
              className="px-3 py-2 flex items-baseline gap-3"
            >
              <span className="text-zinc-500 w-16 flex-shrink-0 truncate" title={r.model_id}>
                {r.model_tier}
              </span>
              <span className="flex-1 min-w-0 truncate">
                {r.doc_title ?? <span className="italic text-zinc-500">deleted doc</span>}
              </span>
              {/* Audit row keeps numeric specifics — this is the "for the
                  curious" surface where exact tokens / credit cost are
                  appropriate. The top of the section is qualitative; this
                  bottom section is the spec sheet. */}
              <span className="text-zinc-500 flex-shrink-0">
                {r.tokens_in + r.tokens_out} tok · {r.credit_cost}c
              </span>
              <span className="text-zinc-500 w-24 flex-shrink-0 text-right">
                {r.ts ? new Date(r.ts).toLocaleDateString() : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TierBadge({
  tier,
  subscriptionStatus,
  tierUntil,
}: {
  tier: string;
  subscriptionStatus: string | null;
  tierUntil: string | null;
}) {
  let label = tier.toUpperCase();
  let cls = "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300";

  if (tier === "pro") {
    cls = "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300";
    if (subscriptionStatus === "canceled" && tierUntil) {
      const date = new Date(tierUntil);
      label = `PRO until ${date.toLocaleDateString()}`;
    } else if (subscriptionStatus === "past_due") {
      label = "PRO (PAYMENT DUE)";
      cls = "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300";
    }
  } else if (tier === "internal") {
    cls = "bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-300";
  }

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}
